'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

interface AudioFileData {
  url: string
  s3Key: string
  file: File
  transcription?: string
}

interface UseVoiceRecordingProps {
  onAudioRecorded?: (audioData: AudioFileData) => void
  onTranscription?: (text: string) => void // Deprecated: use onAudioRecorded
}

interface VoiceRecordingState {
  isRecording: boolean
  isProcessing: boolean
  error: string | null
}

// Feature detection function
function isVoiceRecordingSupported(): { supported: boolean; reason?: string } {
  if (typeof window === 'undefined') {
    return { supported: false, reason: 'Not running in browser environment' }
  }

  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { supported: false, reason: 'getUserMedia not supported' }
  }

  if (!window.MediaRecorder) {
    return { supported: false, reason: 'MediaRecorder not supported' }
  }

  // Check if we're on HTTPS (required for getUserMedia in production)
  if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
    return { supported: false, reason: 'HTTPS required for microphone access' }
  }

  return { supported: true }
}

export function useVoiceRecording({
  onAudioRecorded,
  onTranscription,
}: UseVoiceRecordingProps = {}) {
  const [state, setState] = useState<VoiceRecordingState>({
    isRecording: false,
    isProcessing: false,
    error: null,
  })
  // Always start with unsupported to ensure consistent SSR/hydration
  const [supportInfo, setSupportInfo] = useState<{
    supported: boolean
    reason?: string
  }>({ supported: false, reason: 'Checking browser support...' })

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    const result = isVoiceRecordingSupported()
    setSupportInfo(result)

    if (!result.supported) {
      setState(prev => {
        const nextError = result.reason || 'Voice recording not supported'
        if (prev.error === nextError) {
          return prev
        }

        return {
          ...prev,
          error: prev.error ?? nextError,
        }
      })
    }
  }, [])

  const processAudio = useCallback(
    async (audioBlob: Blob) => {
      try {
        setState(prev => ({ ...prev, isProcessing: true, error: null }))

        // Validate audio blob
        if (!audioBlob || audioBlob.size === 0) {
          throw new Error('No audio data recorded')
        }

        // Create form data
        const formData = new FormData()
        formData.append('file', audioBlob, 'recording.webm')

        // Import the API functions
        const { api } = await import('@/lib/api')

        // Send to transcription API
        const response = await api.upload.uploadFile(formData)

        if (!response.success) {
          throw new Error(response.error || 'Failed to upload audio file')
        }

        const data = response.data

        // Pass audio file data to callback
        if (data?.url && data?.s3Key && onAudioRecorded) {
          const audioFile = new File(
            [audioBlob],
            data.filename || 'recording.webm',
            {
              type: audioBlob.type,
            }
          )
          onAudioRecorded({
            url: data.url,
            s3Key: data.s3Key,
            file: audioFile,
            transcription: data.transcription,
          })
        }

        // Backward compatibility: also call onTranscription if provided
        if (data?.transcription && onTranscription) {
          onTranscription(data.transcription)
        }

        setState(prev => ({ ...prev, isProcessing: false }))
      } catch (error) {
        console.error('Failed to process audio:', error)

        let errorMessage = 'Failed to process audio'
        if (error instanceof Error) {
          if (error.message.includes('fetch')) {
            errorMessage =
              'Network error. Please check your connection and try again.'
          } else if (error.message.includes('Server error')) {
            errorMessage = 'Server error. Please try again later.'
          } else {
            errorMessage = error.message
          }
        }

        setState(prev => ({
          ...prev,
          error: errorMessage,
          isProcessing: false,
        }))
      }
    },
    [onAudioRecorded, onTranscription]
  )

  const startRecording = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, error: null }))

      // Check if MediaRecorder is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Media recording is not supported in this browser')
      }

      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder is not supported in this browser')
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      })

      streamRef.current = stream

      // Determine supported MIME type
      let mimeType = 'audio/webm;codecs=opus'
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm'
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4'
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = '' // Let browser choose
          }
        }
      }

      // Create MediaRecorder with fallback options
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      // Handle data available
      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: mediaRecorder.mimeType || 'audio/webm',
          })
          await processAudio(audioBlob)
        } catch (error) {
          console.error('Failed to process audio after recording:', error)
          setState(prev => ({
            ...prev,
            error: 'Failed to process recorded audio',
            isProcessing: false,
          }))
        }

        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
          streamRef.current = null
        }
      }

      // Handle recording errors
      mediaRecorder.onerror = event => {
        console.error('MediaRecorder error:', event)
        setState(prev => ({
          ...prev,
          error: 'Recording failed due to an error',
          isRecording: false,
        }))
      }

      // Start recording
      mediaRecorder.start(100) // Collect data every 100ms
      setState(prev => ({ ...prev, isRecording: true }))
    } catch (error) {
      console.error('Failed to start recording:', error)

      let errorMessage = 'Failed to start recording'
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage =
            'Microphone access denied. Please allow microphone access and try again.'
        } else if (error.name === 'NotFoundError') {
          errorMessage =
            'No microphone found. Please connect a microphone and try again.'
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'Audio recording is not supported in this browser.'
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'Microphone is already in use by another application.'
        } else {
          errorMessage = error.message
        }
      }

      setState(prev => ({
        ...prev,
        error: errorMessage,
        isRecording: false,
      }))
    }
  }, [processAudio])

  const stopRecording = useCallback(() => {
    try {
      if (mediaRecorderRef.current && state.isRecording) {
        mediaRecorderRef.current.stop()
        setState(prev => ({
          ...prev,
          isRecording: false,
          isProcessing: true,
        }))
      }
    } catch (error) {
      console.error('Failed to stop recording:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to stop recording',
        isRecording: false,
        isProcessing: false,
      }))

      // Clean up stream if there's an error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    }
  }, [state.isRecording])

  const toggleRecording = useCallback(() => {
    if (state.isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }, [state.isRecording, startRecording, stopRecording])

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  return {
    ...state,
    startRecording,
    stopRecording,
    toggleRecording,
    clearError,
    isSupported: supportInfo.supported,
  }
}
